import { useState, useEffect } from 'react'
import { Plus, Search, Pencil, Trash2, Paperclip, Download, Calendar, X, Wrench, AlertTriangle } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import { AllegatiModal } from '../../../components/allegati/AllegatiModal'
import { ScadenzeModal } from '../../../components/strumenti/ScadenzeModal'
import { BulkDownloadStrumentiModal } from '../../../components/strumenti/BulkDownloadStrumentiModal'
import SchedePerEntita from '../../../components/schede/SchedePerEntita'
import { Strumento, StrumentoForm, TIPOLOGIE_STRUMENTO } from '../../../types/strumenti'

const emptyForm: StrumentoForm = {
  tipologia: '',
  descrizione: '',
  data_acquisto: '',
  necessaria_taratura: false,
  note: ''
}

export default function StrumentiPage() {
  const [strumenti, setStrumenti] = useState<Strumento[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterTaratura, setFilterTaratura] = useState<'all' | 'yes' | 'no'>('all')
  const [filterTipologia, setFilterTipologia] = useState('')

  // CRUD
  const [showModal, setShowModal] = useState(false)
  const [editingStrumento, setEditingStrumento] = useState<Strumento | null>(null)
  const [form, setForm] = useState<StrumentoForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<Strumento | null>(null)

  // Allegati
  const [showAllegati, setShowAllegati] = useState(false)
  const [allegatiStrumentoId, setAllegatiStrumentoId] = useState('')

  // Scadenze
  const [showScadenze, setShowScadenze] = useState(false)
  const [scadenzeStrumento, setScadenzeStrumento] = useState<Strumento | null>(null)

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showBulkDownload, setShowBulkDownload] = useState(false)

  useEffect(() => {
    loadStrumenti()
  }, [])

  const loadStrumenti = async () => {
    try {
      setLoading(true)
      const res = await api.get('/v1/strumenti')
      setStrumenti(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  // Filtro
  const filteredStrumenti = strumenti.filter(s => {
    const matchSearch = !search ||
      s.descrizione.toLowerCase().includes(search.toLowerCase()) ||
      (s.tipologia?.toLowerCase() || '').includes(search.toLowerCase())
    const matchTaratura = filterTaratura === 'all' ||
      (filterTaratura === 'yes' && s.necessaria_taratura) ||
      (filterTaratura === 'no' && !s.necessaria_taratura)
    const matchTipologia = !filterTipologia || s.tipologia === filterTipologia
    return matchSearch && matchTaratura && matchTipologia
  })

  // CRUD handlers
  const handleCreate = () => {
    setEditingStrumento(null)
    setForm(emptyForm)
    setError('')
    setShowModal(true)
  }

  const handleEdit = (strumento: Strumento) => {
    setEditingStrumento(strumento)
    setForm({
      tipologia: strumento.tipologia || '',
      descrizione: strumento.descrizione,
      data_acquisto: strumento.data_acquisto || '',
      necessaria_taratura: strumento.necessaria_taratura,
      note: strumento.note || ''
    })
    setError('')
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSaving(true)

    try {
      const data: Record<string, any> = {
        descrizione: form.descrizione,
        necessaria_taratura: form.necessaria_taratura,
      }

      // Campi opzionali - invia solo se compilati
      if (form.tipologia) data.tipologia = form.tipologia
      if (form.data_acquisto) data.data_acquisto = form.data_acquisto
      if (form.note) data.note = form.note

      // In edit mode, invia anche i campi vuoti per permettere la cancellazione
      if (editingStrumento) {
        data.tipologia = form.tipologia || null
        data.data_acquisto = form.data_acquisto || null
        data.note = form.note || null
      }

      if (editingStrumento) {
        await api.put(`/v1/strumenti/${editingStrumento.id}`, data)
        setToast({ message: 'Strumento aggiornato', type: 'success' })
      } else {
        await api.post('/v1/strumenti', data)
        setToast({ message: 'Strumento creato', type: 'success' })
      }

      setShowModal(false)
      loadStrumenti()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return
    try {
      await api.delete(`/v1/strumenti/${confirmDelete.id}`)
      setToast({ message: 'Strumento eliminato', type: 'success' })
      setSelectedIds(prev => {
        const next = new Set(prev)
        next.delete(confirmDelete.id)
        return next
      })
      loadStrumenti()
    } catch {
      setToast({ message: "Errore nell'eliminazione", type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  // Selezione multipla
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredStrumenti.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredStrumenti.map(s => s.id)))
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT')
  }

  return (
    <>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Elimina Strumento"
        message={`Sei sicuro di voler eliminare "${confirmDelete?.descrizione}"? Verranno eliminati anche tutte le scadenze e i documenti associati.`}
        confirmText="Elimina"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDelete(null)}
        variant="danger"
      />

      <AllegatiModal
        isOpen={showAllegati}
        onClose={() => setShowAllegati(false)}
        entityType="strumenti"
        entityId={allegatiStrumentoId}
      />

      {scadenzeStrumento && (
        <ScadenzeModal
          isOpen={showScadenze}
          onClose={() => { setShowScadenze(false); setScadenzeStrumento(null); loadStrumenti() }}
          strumentoId={scadenzeStrumento.id}
          strumentoDescrizione={scadenzeStrumento.descrizione}
        />
      )}

      <BulkDownloadStrumentiModal
        isOpen={showBulkDownload}
        onClose={() => setShowBulkDownload(false)}
        strumentoIds={Array.from(selectedIds)}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Strumenti, Macchinari e Mezzi</h1>
          <p className="text-gray-500">
            {filteredStrumenti.length} {filteredStrumenti.length === 1 ? 'strumento' : 'strumenti'}
            {selectedIds.size > 0 && ` · ${selectedIds.size} selezionat${selectedIds.size === 1 ? 'o' : 'i'}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <button
              onClick={() => setShowBulkDownload(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="w-5 h-5" />
              Scarica Documenti ({selectedIds.size})
            </button>
          )}
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuovo
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Ricerca */}
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca per descrizione o tipologia..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Tipologia */}
          <select
            value={filterTipologia}
            onChange={(e) => setFilterTipologia(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Tutte le tipologie</option>
            {TIPOLOGIE_STRUMENTO.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {/* Taratura */}
          <select
            value={filterTaratura}
            onChange={(e) => setFilterTaratura(e.target.value as 'all' | 'yes' | 'no')}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">Tutte</option>
            <option value="yes">Necessaria taratura</option>
            <option value="no">Non necessaria taratura</option>
          </select>
        </div>

        {/* Select all */}
        {filteredStrumenti.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
              <input
                type="checkbox"
                checked={selectedIds.size === filteredStrumenti.length && filteredStrumenti.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              Seleziona tutti ({filteredStrumenti.length})
            </label>
          </div>
        )}
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Caricamento...</div>
      ) : filteredStrumenti.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Wrench className="w-16 h-16 mx-auto mb-3 opacity-50" />
          <p className="text-lg">{search || filterTaratura !== 'all' || filterTipologia ? 'Nessun risultato' : 'Nessuno strumento inserito'}</p>
          {!search && filterTaratura === 'all' && !filterTipologia && (
            <p className="text-sm mt-1">Clicca "Nuovo" per aggiungere uno strumento</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredStrumenti.map(strumento => (
            <div
              key={strumento.id}
              className={`bg-white rounded-xl shadow-sm border-2 transition-colors ${
                selectedIds.has(strumento.id) ? 'border-primary-300 bg-primary-50/30' : 'border-transparent'
              }`}
            >
              <div className="p-5">
                {/* Top: Checkbox + Icon + Info */}
                <div className="flex items-start gap-3 mb-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(strumento.id)}
                    onChange={() => toggleSelection(strumento.id)}
                    className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {strumento.descrizione}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {strumento.tipologia && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                          {strumento.tipologia}
                        </span>
                      )}
                      {strumento.necessaria_taratura && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Taratura
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="space-y-1.5 mb-4 pl-7">
                  {strumento.data_acquisto && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      Acquistato il {formatDate(strumento.data_acquisto)}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {strumento.scadenze_attive_count > 0 ? (
                      <>
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-amber-600 font-medium">
                          {strumento.scadenze_count} scadenz{strumento.scadenze_count === 1 ? 'a' : 'e'}
                          {' '}({strumento.scadenze_attive_count} attiv{strumento.scadenze_attive_count === 1 ? 'a' : 'e'})
                        </span>
                      </>
                    ) : (
                      <>
                        <Calendar className="w-3.5 h-3.5 text-gray-400" />
                        <span>{strumento.scadenze_count} scadenz{strumento.scadenze_count === 1 ? 'a' : 'e'}</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => { setScadenzeStrumento(strumento); setShowScadenze(true) }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Scadenze"
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="hidden xl:inline">Scadenze</span>
                  </button>
                  <button
                    onClick={() => { setAllegatiStrumentoId(strumento.id); setShowAllegati(true) }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Documenti"
                  >
                    <Paperclip className="w-4 h-4" />
                    <span className="hidden xl:inline">Documenti</span>
                  </button>
                  <div className="flex-1" />
                  <button
                    onClick={() => handleEdit(strumento)}
                    className="p-2 text-gray-400 hover:text-primary-600 rounded-lg transition-colors"
                    title="Modifica"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(strumento)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Schede Dinamiche */}
                <SchedePerEntita
                  entityType="strumento"
                  entityId={strumento.id}
                  entityLabel={strumento.descrizione}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modale Crea/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl z-10">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingStrumento ? 'Modifica Strumento' : 'Nuovo Strumento'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Tipologia */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipologia</label>
                <select
                  value={form.tipologia}
                  onChange={(e) => setForm({ ...form, tipologia: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                >
                  <option value="">Seleziona tipologia...</option>
                  {TIPOLOGIE_STRUMENTO.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione *</label>
                <input
                  type="text"
                  required
                  value={form.descrizione}
                  onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                  placeholder="es. Trapano a colonna, Furgone aziendale..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Data Acquisto / Taratura */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Acquisto</label>
                  <input
                    type="date"
                    value={form.data_acquisto}
                    onChange={(e) => setForm({ ...form, data_acquisto: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.necessaria_taratura}
                      onChange={(e) => setForm({ ...form, necessaria_taratura: e.target.checked })}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Necessaria taratura</span>
                  </label>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  rows={3}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Note aggiuntive..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
